import { Box, Divider, ScrollArea } from "@mantine/core";
import { useMatchRoute, useParams } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";

import { useIdealDeployment } from "@/api/resources/deployments/hooks";
import { useCurrentWorkspace } from "@/api/resources/workspaces/hooks";
import { NavbarListGroupItem } from "@/components/Navbar/NavbarListGroupItem";
import NavbarListItem from "@/components/Navbar/NavbarListItem";
import {
  isNavigationGroup,
  useNavigationElementsDeployment,
  useNavigationElementsWorkspace,
} from "@/components/Navbar/useNavigationElements";
import { useIsMobile } from "@/hooks/useIsMobile";
import { BusinessType } from "@/supabase.types";

const TrialBanner = lazy(() => import("@/components/Onboarding/TrialBanner"));

const WorkspaceSelectorTree = lazy(() => import("@/components/Workspace/WorkspaceSelectorTree"));

interface Props {
  onClose?: () => void;
}
export const NavbarList = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { isTablet } = useIsMobile();
  const matchRoute = useMatchRoute();
  const { workspace_id } = useParams({ strict: false });
  const { data: currentWorkspace } = useCurrentWorkspace();
  const deployment = useIdealDeployment();

  let canManageChildWorkspaces = false;
  if (currentWorkspace?.business_type === BusinessType.service_provider) {
    canManageChildWorkspaces = true;
  }
  if (currentWorkspace?.business_type === BusinessType.manufacturer) {
    canManageChildWorkspaces = true;
  }

  const navigationElementsWorkspace = useNavigationElementsWorkspace(
    workspace_id,
    deployment?.id,
    canManageChildWorkspaces,
  );
  const navigationElementsDeployment = useNavigationElementsDeployment(workspace_id);

  if (!workspace_id) return null;

  return (
    <ScrollArea.Autosize>
      <Suspense>
        <TrialBanner />
      </Suspense>

      <Divider my="xs" px="xs" label={t("workspace")} />
      {isTablet && (
        <Box py="sm">
          <WorkspaceSelectorTree onSelect={onClose} />
        </Box>
      )}
      {navigationElementsWorkspace.map((navElement) => {
        if (isNavigationGroup(navElement)) {
          return (
            <NavbarListGroupItem key={navElement.title} group={navElement} matchRoute={matchRoute} onClose={onClose} />
          );
        }
        return <NavbarListItem key={navElement.title} navigationItem={navElement} onClose={onClose} />;
      })}

      <Divider my="xs" px="xs" label={t("resources")} />

      {navigationElementsDeployment.map((navElement) => {
        if (isNavigationGroup(navElement)) {
          return (
            <NavbarListGroupItem key={navElement.title} group={navElement} matchRoute={matchRoute} onClose={onClose} />
          );
        }
        return <NavbarListItem key={navElement.title} navigationItem={navElement} onClose={onClose} />;
      })}
    </ScrollArea.Autosize>
  );
};

export default NavbarList;
